import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { InputTextarea } from "primereact/inputtextarea";
import { Toast } from "primereact/toast";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  deleteNotifyService,
  getAllNotifyService,
  updateNotifyService,
} from "../../services/Notify/Notify";
import { showError, showSuccess } from "../Toast/Toast.functions";
import AdminPageHeader from "./AdminPageHeader";

interface AdminNotify {
  _id: string;
  emailCustomer: string;
  customerName?: string;
  customerPhoneNumber?: string;
  suppliers: any[];
  alreadySent: boolean;
  sentWithErrors: boolean;
  text: string;
  type: number;
}

const AdminNotifications = () => {
  const { t } = useTranslation(["common"]);
  const toast = useRef<Toast>(null);

  const notifyTypeLabels: Record<number, string> = {
    0: t("common:notifyTypeCustomerConfirmation"),
    1: t("common:notifyTypeSupplierRequest"),
    2: t("common:notifyTypeAdminAlert"),
  };

  const [notifications, setNotifications] = useState<AdminNotify[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [onlyUnsent, setOnlyUnsent] = useState<boolean>(false);

  const [editingNotify, setEditingNotify] = useState<AdminNotify | null>(null);
  const [deletingNotify, setDeletingNotify] = useState<AdminNotify | null>(null);

  const loadAll = () => {
    setLoading(true);
    getAllNotifyService()
      .then((res: any) => setNotifications(res.data ?? []))
      .catch((err) => console.error(err))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadAll();
  }, []);

  const filteredNotifications = useMemo(() => {
    const search = globalFilterValue.trim().toLowerCase();
    return notifications.filter((n) => {
      if (onlyUnsent && n.alreadySent) return false;
      if (!search) return true;
      return (
        n.emailCustomer?.toLowerCase().includes(search) ||
        n.customerName?.toLowerCase().includes(search) ||
        n.text?.toLowerCase().includes(search)
      );
    });
  }, [notifications, globalFilterValue, onlyUnsent]);

  const submitEdit = () => {
    if (!editingNotify) return;
    updateNotifyService(editingNotify)
      .then(() => {
        showSuccess(toast, t("common:Save"));
        setEditingNotify(null);
        loadAll();
      })
      .catch(() => showError(toast, t("common:genericError")));
  };

  const confirmDelete = () => {
    if (!deletingNotify) return;
    deleteNotifyService(deletingNotify)
      .then(() => {
        setNotifications((prev) => prev.filter((n) => n._id !== deletingNotify._id));
        setDeletingNotify(null);
      })
      .catch(() => showError(toast, t("common:genericError")));
  };

  return (
    <div className="admin-page">
      <Toast ref={toast} position="top-right" />
      <AdminPageHeader title={t("common:notifications")} />

      <div className="admin-page-body">
      <div className="flex align-items-center mb-3" style={{ gap: "1rem" }}>
        <span className="p-input-icon-left">
          <i className="pi pi-search" />
          <InputText
            value={globalFilterValue}
            onChange={(e) => setGlobalFilterValue(e.target.value)}
            placeholder="Keyword Search"
          />
        </span>
        <div className="flex align-items-center" style={{ gap: "0.5rem" }}>
          <Checkbox
            inputId="onlyUnsent"
            checked={onlyUnsent}
            onChange={(e) => setOnlyUnsent(!!e.checked)}
          />
          <label htmlFor="onlyUnsent">{t("common:onlyUnsent")}</label>
        </div>
      </div>

      <DataTable
        value={filteredNotifications}
        loading={loading}
        paginator
        rows={20}
        dataKey="_id"
        emptyMessage={t("common:noResults")}
      >
        <Column field="emailCustomer" header={t("common:Email")} />
        <Column field="customerName" header={t("common:Name")} />
        <Column
          field="type"
          header={t("common:type")}
          body={(row: AdminNotify) => notifyTypeLabels[row.type] ?? row.type}
        />
        <Column
          field="alreadySent"
          header={t("common:sent")}
          body={(row: AdminNotify) => (row.alreadySent ? t("common:Yes") : t("common:No"))}
        />
        <Column
          field="sentWithErrors"
          header={t("common:sentWithErrors")}
          body={(row: AdminNotify) => (row.sentWithErrors ? t("common:Yes") : t("common:No"))}
        />
        <Column
          body={(row: AdminNotify) => (
            <div className="admin-row-actions">
              <Button
                icon="pi pi-pencil"
                className="p-button-rounded p-button-text"
                onClick={() => setEditingNotify({ ...row })}
              />
              <Button
                icon="pi pi-trash"
                className="p-button-rounded p-button-warning"
                onClick={() => setDeletingNotify(row)}
              />
            </div>
          )}
        />
      </DataTable>
      </div>

      <Dialog
        header={t("common:notifications")}
        visible={editingNotify != null}
        onHide={() => setEditingNotify(null)}
        style={{ width: "40vw" }}
        footer={
          <div className="flex justify-content-space-between">
            <Button className="general-button" onClick={() => setEditingNotify(null)}>
              {t("common:undo")}
            </Button>
            <Button className="general-button" onClick={submitEdit}>
              {t("common:Save")}
            </Button>
          </div>
        }
      >
        {editingNotify && (
          <div className="p-fluid">
            <div className="field">
              <label>{t("common:Email")}</label>
              <InputText
                value={editingNotify.emailCustomer}
                onChange={(e) =>
                  setEditingNotify({ ...editingNotify, emailCustomer: e.target.value })
                }
              />
            </div>
            <div className="field">
              <label>{t("common:Name")}</label>
              <InputText
                value={editingNotify.customerName ?? ""}
                onChange={(e) =>
                  setEditingNotify({ ...editingNotify, customerName: e.target.value })
                }
              />
            </div>
            <div className="field">
              <label>{t("common:Phone")}</label>
              <InputText
                value={editingNotify.customerPhoneNumber ?? ""}
                onChange={(e) =>
                  setEditingNotify({
                    ...editingNotify,
                    customerPhoneNumber: e.target.value,
                  })
                }
              />
            </div>
            <div className="field">
              <label>{t("common:text")}</label>
              <InputTextarea
                rows={4}
                value={editingNotify.text}
                onChange={(e) =>
                  setEditingNotify({ ...editingNotify, text: e.target.value })
                }
              />
            </div>
            <div className="field flex align-items-center" style={{ gap: "0.5rem" }}>
              <Checkbox
                inputId="alreadySent"
                checked={editingNotify.alreadySent}
                onChange={(e) =>
                  setEditingNotify({ ...editingNotify, alreadySent: !!e.checked })
                }
              />
              <label htmlFor="alreadySent">{t("common:sent")}</label>
            </div>
          </div>
        )}
      </Dialog>

      <Dialog
        onHide={() => setDeletingNotify(null)}
        visible={deletingNotify != null}
        closable={false}
        style={{ maxWidth: "50vh" }}
        footer={
          <div className="flex justify-content-space-between">
            <Button className="general-button" onClick={() => setDeletingNotify(null)}>
              {t("common:No")}
            </Button>
            <Button className="general-button" onClick={confirmDelete}>
              {t("common:Yes")}
            </Button>
          </div>
        }
      >
        {deletingNotify?.emailCustomer}
      </Dialog>
    </div>
  );
};

export default AdminNotifications;
